import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { Share, Plus, Smartphone, Download, Check, ChevronRight, MapPin, Sparkles, Compass } from "lucide-react";

/**
 * Public installation page for CLIENTS (end customers).
 *
 * URL: /cliente/instalar (public, no auth).
 *
 * Reuses the same PWA manifest as the driver app but funnels installed clients
 * to `/app` (the ClientApp home).
 */
export default function ClientInstall() {
  const [platform, setPlatform] = useState("unknown");
  const [installed, setInstalled] = useState(false);
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [pageUrl, setPageUrl] = useState("");

  useEffect(() => {
    // Always show the production URL in the QR so drivers/clients land on
    // ditours.pt (not the preview host).
    const PROD_HOST = "https://ditours.pt";
    setPageUrl(`${PROD_HOST}/cliente/instalar`);
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    const isMobile = isIOS || isAndroid || /Mobile/i.test(ua);
    setPlatform(isIOS ? "ios" : isAndroid ? "android" : isMobile ? "mobile" : "desktop");

    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      // eslint-disable-next-line no-undef
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);

    const onBeforeInstall = (e) => { e.preventDefault(); setInstallPromptEvt(e); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const triggerInstall = async () => {
    if (!installPromptEvt) return;
    installPromptEvt.prompt();
    const { outcome } = await installPromptEvt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setInstallPromptEvt(null);
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#0a0f1e" }} data-testid="client-install-page">
      {/* Hero */}
      <div
        className="relative overflow-hidden"
        style={{
          backgroundImage: "linear-gradient(135deg, rgba(10,15,30,0.85) 0%, rgba(10,15,30,0.6) 60%, rgba(245,158,11,0.4) 100%), url(https://images.unsplash.com/photo-1596703263847-15dc0e0aa4c2?auto=format&fit=crop&w=1600&q=70)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="px-6 pt-14 pb-12 max-w-md mx-auto">
          <div className="text-3xl font-display tracking-wide">
            DI<span style={{ color: "#f59e0b" }}>TOURS</span>
          </div>
          <div className="mt-2 text-[11px] tracking-[0.3em] uppercase opacity-70">A sua app de viagem</div>

          <h1 className="mt-8 font-display text-4xl leading-tight">Instale a app DiTours no seu telemóvel</h1>
          <p className="mt-3 text-sm opacity-90 leading-relaxed">
            Reserve transferes, encontre tours perto de si, acompanhe o motorista em tempo real e receba
            notificações — tudo num só toque, como uma app nativa.
          </p>
        </div>
      </div>

      <div className="px-6 pb-24 max-w-md mx-auto -mt-6">
        {/* Benefits (rendered first — always visible) */}
        <div className="grid grid-cols-1 gap-3 mb-8">
          <Benefit icon={MapPin} title="Tours perto de si" body="Localização automática mostra experiências dentro do raio que escolher." />
          <Benefit icon={Sparkles} title="Reservas 1-toque" body="Transferes com preço em tempo real via Google Maps." />
          <Benefit icon={Compass} title="Acompanhamento" body="Veja o motorista a chegar. Confirme pagamento no telemóvel." />
        </div>

        {/* Installed state */}
        {installed && (
          <div className="border border-emerald-400 bg-emerald-400/10 p-6 text-center" data-testid="client-install-done">
            <Check className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
            <div className="font-display text-xl mb-2">App já instalada</div>
            <div className="text-sm opacity-70 mb-5">Toque no ícone DiTours no ecrã principal para começar.</div>
            <Link to="/app" className="inline-flex items-center gap-2 h-11 px-6 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs tracking-wide" data-testid="client-open-app">
              Abrir app <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {!installed && platform === "android" && installPromptEvt && (
          <div className="border-2 border-[#f59e0b] bg-[#f59e0b]/10 p-6" data-testid="client-android-prompt">
            <div className="font-display text-2xl mb-2">Instalar em 1 toque</div>
            <div className="text-sm opacity-80 mb-5">Toque no botão abaixo e confirme "Instalar".</div>
            <button onClick={triggerInstall} data-testid="client-android-btn"
              className="w-full h-14 bg-[#f59e0b] text-[#0a0f1e] font-bold uppercase text-sm tracking-wide flex items-center justify-center gap-2">
              <Download className="w-5 h-5" /> Instalar DiTours
            </button>
          </div>
        )}

        {!installed && platform === "android" && !installPromptEvt && (
          <Steps
            title="Instalar no Android"
            steps={[
              { icon: <Smartphone className="w-5 h-5" />, text: "Toque nos 3 pontos ⋮ no topo do browser." },
              { icon: <Download className="w-5 h-5" />, text: "Toque em «Instalar app» ou «Adicionar ao ecrã principal»." },
              { icon: <Check className="w-5 h-5" />, text: "Confirme. O ícone DiTours aparece no ecrã principal." },
            ]}
            testId="client-android-manual"
          />
        )}

        {!installed && platform === "ios" && (
          <Steps
            title="Instalar no iPhone"
            steps={[
              { icon: <Share className="w-5 h-5" />, text: "Toque no botão «Partilhar» (ícone quadrado ↑) na barra do Safari." },
              { icon: <Plus className="w-5 h-5" />, text: "Deslize e toque em «Adicionar ao Ecrã Principal»." },
              { icon: <Check className="w-5 h-5" />, text: "Toque em «Adicionar». O ícone DiTours fica no seu ecrã." },
            ]}
            testId="client-ios-manual"
            note="Abra esta página no Safari — a instalação não funciona no Chrome no iPhone."
          />
        )}

        {!installed && (platform === "desktop" || platform === "unknown") && (
          <div className="border border-white/20 bg-white/5 p-6 text-center" data-testid="client-install-desktop">
            <div className="font-display text-xl mb-3">Escaneie no seu telemóvel</div>
            <div className="text-sm opacity-70 mb-5">Aponte a câmara do telemóvel para o código.</div>
            <div className="inline-block bg-white p-3 rounded">
              {pageUrl ? <QRCodeSVG value={pageUrl} size={220} level="M" /> : <div className="w-[220px] h-[220px] bg-white" />}
            </div>
            <div className="mt-5 text-xs font-mono opacity-60 break-all">{pageUrl}</div>
          </div>
        )}

        {!installed && platform === "mobile" && (
          <Steps
            title="Instalar no telemóvel"
            steps={[
              { icon: <Smartphone className="w-5 h-5" />, text: "Abra o menu do browser." },
              { icon: <Download className="w-5 h-5" />, text: "«Adicionar ao ecrã principal» ou «Instalar app»." },
              { icon: <Check className="w-5 h-5" />, text: "Confirme. Ícone DiTours no ecrã." },
            ]}
            testId="client-mobile-generic"
          />
        )}

        <div className="mt-8 text-center">
          <Link to="/app" data-testid="client-continue-browser" className="text-xs uppercase tracking-widest text-white/60 hover:text-[#f59e0b]">
            ou continue no browser →
          </Link>
        </div>

        <div className="mt-10 text-center text-xs opacity-50">
          É motorista? <Link to="/motorista/instalar" className="text-[#f59e0b] underline underline-offset-4">Instalar app de motorista</Link>
        </div>
      </div>
    </div>
  );
}

function Steps({ title, steps, testId, note }) {
  return (
    <div className="border border-white/20 bg-white/5 p-6" data-testid={testId}>
      <div className="font-display text-2xl mb-5">{title}</div>
      <ol className="space-y-4">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 border border-[#f59e0b] text-[#f59e0b] flex items-center justify-center font-display">
              {i + 1}
            </div>
            <div className="flex-1 pt-1">
              <div className="flex items-center gap-2 text-sm text-white">
                {s.icon}
                <span>{s.text}</span>
              </div>
            </div>
          </li>
        ))}
      </ol>
      {note && <div className="mt-5 pt-4 border-t border-white/10 text-xs opacity-70">{note}</div>}
    </div>
  );
}

function Benefit({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3 border-l-2 border-[#f59e0b] bg-white/5 p-4">
      <Icon className="w-5 h-5 text-[#f59e0b] flex-shrink-0 mt-0.5" />
      <div>
        <div className="font-display text-sm text-[#f59e0b] uppercase tracking-wide mb-1">{title}</div>
        <div className="text-xs opacity-80">{body}</div>
      </div>
    </div>
  );
}
